import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import Home from "@/pages/Home";
import Projects from "@/pages/Projects";
import Showroom from "@/pages/Showroom";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import TestimonialsPage from "@/pages/TestimonialsPage";
import SpitfireDiscontinued from "@/pages/SpitfireDiscontinued";
import BrandProductPage from "@/pages/BrandProductPage";
import ProfessionalPage from "@/pages/ProfessionalPage";
import AreasIndex from "@/pages/AreasIndex";
import AreaPage from "@/pages/AreaPage";
import AdminAreas from "@/pages/AdminAreas";
import AdminAreaEditor from "@/pages/admin/AdminAreaEditor";
import ProjectJournal from "@/pages/ProjectJournal";
import AdminPages from "@/pages/admin/AdminPages";
import AdminPageEditor from "@/pages/admin/AdminPageEditor";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminCaseStudies from "@/pages/admin/AdminCaseStudies";
import AdminSiteContent from "@/pages/admin/AdminSiteContent";
import { AdminLogin, AdminDashboard, RequireAdmin } from "@/pages/admin/AdminCore";
import { BRAND_PAGES, BRAND_ALIASES } from "@/data/brandPages";
import { initSeo, getRedirect } from "@/lib/seo";
import { initSiteContent } from "@/lib/siteContent";

const SPITFIRE_URLS = ["/spitfire-doors/", "/entrance-doors/spitfire-doors/"];

const BRAND_ROUTES = [...Object.keys(BRAND_PAGES), ...Object.keys(BRAND_ALIASES)];

const RedirectWatcher = () => {
  const { pathname } = useLocation();
  const to = getRedirect(pathname);
  return to ? <Navigate to={to} replace /> : null;
};

function App() {
  const [seoReady, setSeoReady] = useState(false);
  const [, bump] = useState(0);

  useEffect(() => {
    Promise.allSettled([initSeo(), initSiteContent(() => bump((v) => v + 1))]).finally(() => setSeoReady(true));
    const hideBroken = (e) => {
      if (e.target?.tagName === "IMG") e.target.style.visibility = "hidden";
    };
    const showLoaded = (e) => {
      if (e.target?.tagName === "IMG" && e.target.style.visibility === "hidden") e.target.style.visibility = "";
    };
    window.addEventListener("error", hideBroken, true);
    window.addEventListener("load", showLoaded, true);
    return () => { window.removeEventListener("error", hideBroken, true); window.removeEventListener("load", showLoaded, true); };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App grain">
      <BrowserRouter>
        {seoReady && <Navbar />}
        {seoReady && <RedirectWatcher />}
        {seoReady && (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/architects/" element={<ProfessionalPage />} />
          <Route path="/developers/" element={<ProfessionalPage />} />
          <Route path="/quantity-surveyors/" element={<ProfessionalPage />} />
          <Route path="/areas/" element={<AreasIndex />} />
          <Route path="/areas/:slug/" element={<AreaPage />} />
          <Route path="/areas/:slug" element={<AreaPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/admin/pages" element={<RequireAdmin><AdminPages /></RequireAdmin>} />
          <Route path="/admin/pages/edit" element={<RequireAdmin><AdminPageEditor /></RequireAdmin>} />
          <Route path="/admin/areas" element={<RequireAdmin><AdminAreas /></RequireAdmin>} />
          <Route path="/admin/areas/edit" element={<RequireAdmin><AdminAreaEditor /></RequireAdmin>} />
          <Route path="/admin/seo" element={<RequireAdmin><AdminSeo /></RequireAdmin>} />
          <Route path="/admin/case-studies" element={<RequireAdmin><AdminCaseStudies /></RequireAdmin>} />
          <Route path="/admin/site-content" element={<RequireAdmin><AdminSiteContent /></RequireAdmin>} />
          {BRAND_ROUTES.map((path) => (
            <Route key={path} path={path} element={<BrandProductPage />} />
          ))}
          <Route path="/projects/" element={<Projects />} />
          <Route path="/projects/:slug/" element={<ProjectJournal />} />
          <Route path="/projects/:slug" element={<ProjectJournal />} />
          {SPITFIRE_URLS.map((path) => (
            <Route key={path} path={path} element={<SpitfireDiscontinued />} />
          ))}
          <Route path="/testimonials/" element={<TestimonialsPage />} />
          <Route path="/showroom/" element={<Showroom />} />
          <Route path="/contact/" element={<Contact />} />
          <Route path="/privacy-policy/" element={<Privacy />} />
          <Route path="*" element={<BrandProductPage />} />
        </Routes>
        )}
        {seoReady && <Footer />}
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="elfsight-app-7dcaa6e1-ba81-4ed2-b81a-5958c79c0920" data-elfsight-app-lazy data-testid="whatsapp-widget" />
      <div className="elfsight-app-3d1adbed-ddba-43a4-92c5-448ec02844a5" data-elfsight-app-lazy data-testid="google-reviews-widget" />
    </div>
  );
}

export default App;
